import { DateRange } from "@sonder/ui/source/styled/components/DateRangePicker";
import {
  ScheduleFilterInput,
  SelectedTaskDetails,
  ViewedItem,
} from "schedule/reducer";

export enum ActionType {
  AddBatchJobAction = "ADD_BATCH_JOB",
  AddSequentialJobAction = "ADD_SEQUENTIAL_JOB",
  CloseItem = "CLOSE_ITEM",
  DeselectTasks = "DESELECT_TASKS",
  RemoveBatchJobAction = "REMOVE_BATCH_JOB",
  RemoveSequentialJobAction = "REMOVE_SEQUENTIAL_JOB",
  SelectTasks = "SELECT_TASKS",
  SetDisabled = "SET_DISABLED",
  SetFilters = "SET_FILTERS",
  SetCompletingTask = "SET_COMPLETING_TASK",
  SetHoveringTask = "SET_HOVERED_TASK",
  SetModifyingTask = "SET_MODIFYING_TASK",
  SetSelecting = "SET_SELECTING",
  SetArchivedTask = "SET_ARCHIVED_TASK",
  SetUnscheduledTask = "SET_UNSCHEDULED_TASK",
  UnsetFilters = "UNSET_FILTERS",
  UpdateDateRange = "UPDATE_DATE_RANGE",
  SetPrepKitModalState = "SET_PREP_KIT_MODAL_STATE",
  ViewItem = "VIEW_ITEM",
}

export interface UpdateDateRangeAction {
  type: ActionType.UpdateDateRange;
  payload: { dateRange: DateRange };
}

export const updateDateRange = (
  dateRange: DateRange,
): UpdateDateRangeAction => ({
  type: ActionType.UpdateDateRange,
  payload: { dateRange },
});

export interface SetPrepKitModalStateAction {
  type: ActionType.SetPrepKitModalState;
  payload: { isPrepKitReferenceOpen: boolean };
}

export const setPrepKitModal = (
  isPrepKitReferenceOpen: boolean,
): SetPrepKitModalStateAction => {
  return {
    type: ActionType.SetPrepKitModalState,
    payload: { isPrepKitReferenceOpen },
  };
};

export interface ViewItemAction {
  type: ActionType.ViewItem;
  payload: { items: ViewedItem[] };
}

export const viewItem = (items: ViewedItem[]): ViewItemAction => ({
  type: ActionType.ViewItem,
  payload: { items },
});

export interface CloseItemAction {
  type: ActionType.CloseItem;
  payload: { items: ViewedItem[] };
}

export const closeItem = (items: ViewedItem[]): CloseItemAction => ({
  type: ActionType.CloseItem,
  payload: { items },
});

export interface SetDisabledAction {
  type: ActionType.SetDisabled;
  payload: { isDisabled: boolean };
}

export const setDisabled = (isDisabled: boolean): SetDisabledAction => ({
  type: ActionType.SetDisabled,
  payload: { isDisabled },
});
export interface SetSelectingAction {
  type: ActionType.SetSelecting;
  payload: { isSelecting: boolean };
}

export const setSelecting = (isSelecting: boolean): SetSelectingAction => ({
  type: ActionType.SetSelecting,
  payload: { isSelecting },
});

export interface SelectTasksAction {
  type: ActionType.SelectTasks;
  payload: {
    tasks: SelectedTaskDetails[];
  };
}
export const selectTasks = (
  tasks: SelectedTaskDetails[],
): SelectTasksAction => ({
  type: ActionType.SelectTasks,
  payload: {
    tasks,
  },
});

export interface DeselectTasksAction {
  type: ActionType.DeselectTasks;
  payload: {
    tasks: SelectedTaskDetails[];
  };
}
export const deselectTasks = (
  tasks: SelectedTaskDetails[],
): DeselectTasksAction => ({
  type: ActionType.DeselectTasks,
  payload: {
    tasks,
  },
});

export interface AddBatchJobAction {
  type: ActionType.AddBatchJobAction;
  payload: {
    jobId: string;
  };
}
export const addBatchJob = (jobId: string): AddBatchJobAction => ({
  type: ActionType.AddBatchJobAction,
  payload: {
    jobId,
  },
});

export interface SequentialJobActionVariables {
  assigneeId?: string;
  assigneeIds?: string[];
}

export interface SequentialJobActionTaskDetails {
  id: string;
  internalRef?: string;
}

export interface SequentialJobActionPayload {
  tasks: SequentialJobActionTaskDetails[];
  variables: SequentialJobActionVariables;
}

export interface SequentialJobActionPayloadWithId
  extends SequentialJobActionPayload {
  jobId: string;
}

export interface AddSequentialJobAction {
  type: ActionType.AddSequentialJobAction;
  payload: {
    tasks: SequentialJobActionTaskDetails[];
    variables: SequentialJobActionVariables;
  };
}

export const addSequentialJob = (
  payload: SequentialJobActionPayload,
): AddSequentialJobAction => ({
  type: ActionType.AddSequentialJobAction,
  payload,
});

export interface RemoveBatchJobAction {
  type: ActionType.RemoveBatchJobAction;
  payload: {
    jobId: string;
  };
}
export const removeBatchJob = (jobId: string): RemoveBatchJobAction => ({
  type: ActionType.RemoveBatchJobAction,
  payload: {
    jobId,
  },
});

export interface RemoveSequentialJobAction {
  type: ActionType.RemoveSequentialJobAction;
  payload: {
    jobId: string;
  };
}
export const removeSequentialJob = (
  jobId: string,
): RemoveSequentialJobAction => ({
  type: ActionType.RemoveSequentialJobAction,
  payload: {
    jobId,
  },
});

export interface SetUnscheduledTask {
  type: ActionType.SetUnscheduledTask;
  payload: { taskId: string };
}

export const setUnscheduledTask = (taskId: string): SetUnscheduledTask => ({
  type: ActionType.SetUnscheduledTask,
  payload: { taskId },
});

export interface SetArchivedTask {
  type: ActionType.SetArchivedTask;
  payload: { taskId: string };
}

export const setArchivedTask = (taskId: string): SetArchivedTask => ({
  type: ActionType.SetArchivedTask,
  payload: { taskId },
});

export interface SetFiltersAction {
  type: ActionType.SetFilters;
  payload: ScheduleFilterInput;
}
export const setFilters = (filter: ScheduleFilterInput): SetFiltersAction => ({
  type: ActionType.SetFilters,
  payload: filter,
});

export interface UnsetFiltersAction {
  type: ActionType.UnsetFilters;
  payload: ScheduleFilterInput;
}
export const unsetFilters = (
  filter: ScheduleFilterInput,
): UnsetFiltersAction => ({
  type: ActionType.UnsetFilters,
  payload: filter,
});

export interface SetHoveringTask {
  type: ActionType.SetHoveringTask;
  payload: { taskId: string };
}

export const setHoveringTask = (taskId: string): SetHoveringTask => ({
  type: ActionType.SetHoveringTask,
  payload: { taskId },
});

export interface SetModifyingTask {
  type: ActionType.SetModifyingTask;
  payload: { taskId: string };
}

export const setModifyingTask = (taskId: string): SetModifyingTask => ({
  type: ActionType.SetModifyingTask,
  payload: { taskId },
});

export interface SetCompletingTaskAction {
  type: ActionType.SetCompletingTask;
  payload: { taskId: string };
}

export const setCompletingTask = (taskId: string): SetCompletingTaskAction => ({
  type: ActionType.SetCompletingTask,
  payload: { taskId },
});
